import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { ArrowLeft, Droplets, Waves, ExternalLink, ChevronRight, Calendar, Shirt, AlertTriangle } from "lucide-react";
import IncidentReportPage from "./IncidentReportPage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PoolDashboard({ onLogout }) {
  const [view, setView] = useState("home"); // home | groups | sheets | detail | incident-report
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [sheetDates, setSheetDates] = useState([]);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buddy-sheets/groups`, { headers });
      setGroups(res.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const openGroup = async (group) => {
    setSelectedGroup(group);
    try {
      const res = await axios.get(`${API}/buddy-sheets/archive?group_code=${group.group_code}`, { headers });
      const allSheets = res.data || [];
      // Get unique dates
      const dates = [...new Set(allSheets.map(s => s.date))].sort().reverse();
      setSheetDates(dates);
      setSheets(allSheets);
      setView("sheets");
    } catch (e) { console.error(e); }
  };

  const openDate = (date) => {
    const daySheets = sheets.filter(s => s.date === date);
    setSelectedSheet({ date, am: daySheets.find(s => s.period === "AM"), pm: daySheets.find(s => s.period === "PM") });
    setView("detail");
  };

  const downloadPDF = async (sheetId) => {
    try {
      const response = await fetch(`${API}/buddy-sheets/${sheetId}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `buddy_sheet_${sheetId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) { console.error(e); }
  };

  // ═══ Detail View ═══
  if (view === "detail" && selectedSheet) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50">
        <div className="sticky top-0 z-10 bg-cyan-700 text-white px-4 py-3 shadow-md">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setView("sheets")} className="text-white hover:bg-cyan-600">
                <ArrowLeft className="w-4 h-4 mr-1" />Back
              </Button>
              <h1 className="text-sm font-bold">{selectedGroup?.group_code} — {new Date(selectedSheet.date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric" })}</h1>
            </div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {["AM", "PM"].map(p => {
            const sheet = p === "AM" ? selectedSheet.am : selectedSheet.pm;
            return (
              <Card key={p} className={`${sheet ? "bg-white" : "bg-gray-50 opacity-60"}`} data-testid={`pool-sheet-${p}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Badge variant={p === "AM" ? "default" : "secondary"}>{p}</Badge>
                      {sheet ? sheet.group_name || selectedGroup?.group_name : "No sheet"}
                    </span>
                    {sheet && (
                      <Button size="sm" variant="outline" onClick={() => downloadPDF(sheet.id)} className="text-xs">
                        PDF
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                {sheet && (
                  <CardContent className="pt-0">
                    <div className="text-xs text-muted-foreground">
                      {sheet.rows?.filter(r => r.buddy_a || r.buddy_b).length} rows filled
                      {sheet.comments && <span className="ml-2">• {sheet.comments.slice(0, 50)}</span>}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // ═══ Sheets (Dates) View ═══
  if (view === "sheets" && selectedGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50">
        <div className="sticky top-0 z-10 bg-cyan-700 text-white px-4 py-3 shadow-md">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setView("home")} className="text-white hover:bg-cyan-600">
                <ArrowLeft className="w-4 h-4 mr-1" />Back
              </Button>
              <h1 className="text-sm font-bold">{selectedGroup.group_code} — {selectedGroup.group_name}</h1>
            </div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-2">
          {sheetDates.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-8">No buddy sheets for this group</p>
          ) : (
            sheetDates.map(date => {
              const daySheets = sheets.filter(s => s.date === date);
              const hasAM = daySheets.some(s => s.period === "AM");
              const hasPM = daySheets.some(s => s.period === "PM");
              return (
                <button
                  key={date}
                  onClick={() => openDate(date)}
                  className="w-full flex items-center justify-between p-4 bg-white rounded-lg border hover:bg-cyan-50 transition-colors"
                  data-testid={`pool-date-${date}`}
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-cyan-600" />
                    <span className="font-medium text-sm">{new Date(date + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasAM && <Badge variant="default" className="text-[10px]">AM</Badge>}
                    {hasPM && <Badge variant="secondary" className="text-[10px]">PM</Badge>}
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ═══ Incident Report ═══
  if (view === "incident-report") {
    return (
      <IncidentReportPage
        group={{ code: "POOL", name: "Pool" }}
        mode="pool"
        onBack={() => setView("home")}
      />
    );
  }

  // ═══ Home View ═══
  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-cyan-700 text-white px-4 py-3 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Waves className="w-6 h-6" />
            Pool Dashboard
          </h1>
          <Button variant="ghost" size="sm" onClick={onLogout} className="text-white hover:bg-cyan-600 text-xs">
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Quick Links */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <a
            href="http://rrdcaquatics.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 p-4 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 transition-colors shadow-md"
            data-testid="swim-color-link"
          >
            <Droplets className="w-5 h-5" />
            Swim Colors
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <a
            href="https://rrdcswimlessons.com/admin"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 p-4 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 transition-colors shadow-md"
            data-testid="swim-lessons-link"
          >
            <Waves className="w-5 h-5" />
            Swim Lessons
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <a
            href="https://rrdcstaffsnapshot.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 p-4 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-colors shadow-md"
            data-testid="pool-staff-snapshot-link"
          >
            <Shirt className="w-5 h-5" />
            Staff Snapshot
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button
            onClick={() => setView("incident-report")}
            className="flex items-center justify-center gap-2 p-4 bg-red-600 text-white rounded-xl font-bold text-sm hover:bg-red-700 transition-colors shadow-md"
            data-testid="pool-incident-report-btn"
          >
            <AlertTriangle className="w-5 h-5" />
            File Incident Report
          </button>
        </div>

        {/* Buddy Sheets by Group */}
        <h2 className="text-base font-bold text-cyan-800 mt-4">Buddy Sheets by Group</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {groups.length === 0 ? (
            <p className="col-span-full text-center text-muted-foreground text-sm py-8">No buddy sheets yet</p>
          ) : (
            groups.map(g => (
              <button
                key={g.group_code}
                onClick={() => openGroup(g)}
                className="flex flex-col items-center p-4 bg-white rounded-xl border border-cyan-200 hover:border-cyan-400 hover:shadow-md transition-all"
                data-testid={`pool-group-${g.group_code}`}
              >
                <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-700 font-bold text-lg mb-2">
                  {g.group_code}
                </div>
                <span className="text-xs font-medium text-gray-700">{g.group_name || g.group_code}</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">{g.sheet_count} sheets</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
